import type {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  MouseEvent as ReactMouseEvent,
} from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import type { Point } from "@/lib/geometry";
import { TOOL_IDS } from "@/viewport-tools/measurement";

import { normalizeRect, translateGeometry } from "../measurement-overlay-helpers";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type DraftMeasurementState,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type PolygonGeometry,
} from "../measurement-overlay-types";
import type {
  InteractionState,
  MeasurementOverlayContextMenuState,
  ResizeHandle,
} from "./types";

type PreviewGeometryMap = Record<string, MeasurementGeometry | PolygonGeometry>;

interface StartDraftMoveInteractionArgs {
  event: ReactMouseEvent;
  draft: DraftMeasurementState;
  drawingDraftRef: MutableRefObject<DraftMeasurementState | null>;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
}

export function startDraftMoveInteraction({
  event,
  draft,
  drawingDraftRef,
  getPagePoint,
  interactionRef,
  setInteraction,
  setContextMenu,
}: StartDraftMoveInteractionArgs) {
  if (drawingDraftRef.current) {
    return;
  }

  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  const nextInteraction: InteractionState = {
    mode: "move-draft",
    startPoint: pagePoint,
    initialDraft: draft,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
  setContextMenu(null);
}

interface OpenMeasurementContextMenuInteractionArgs {
  event: ReactMouseEvent;
  measurement: MeasurementOverlayRecord;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  overlayRef: MutableRefObject<HTMLDivElement | null>;
  lastPointerPagePointRef: MutableRefObject<Point | null>;
  handleCancelDraft: () => void;
  onSelectMeasurement: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  setContextMenu: Dispatch<SetStateAction<MeasurementOverlayContextMenuState | null>>;
}

export function openMeasurementContextMenuInteraction({
  event,
  measurement,
  getPagePoint,
  overlayRef,
  lastPointerPagePointRef,
  handleCancelDraft,
  onSelectMeasurement,
  setContextMenu,
}: OpenMeasurementContextMenuInteractionArgs) {
  event.preventDefault();
  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  const overlayRect = overlayRef.current?.getBoundingClientRect();
  if (pagePoint) {
    lastPointerPagePointRef.current = pagePoint;
  }
  handleCancelDraft();
  onSelectMeasurement(measurement._id);
  setContextMenu({
    target: {
      type: "measurement",
      measurementId: measurement._id,
    },
    x: overlayRect ? event.clientX - overlayRect.left : event.clientX,
    y: overlayRect ? event.clientY - overlayRect.top : event.clientY,
    pagePoint,
  });
}

interface StartMeasurementMoveInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  geometry: MeasurementGeometry | PolygonGeometry;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  handleCancelDraft: () => void;
  measurements: MeasurementOverlayRecord[];
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    options?: { shiftKey?: boolean }
  ) => void;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementMoveInteraction({
  event,
  measurementId,
  geometry,
  getPagePoint,
  handleCancelDraft,
  measurements,
  selectedMeasurementIds,
  onSelectMeasurement,
  setPreviewGeometries,
  interactionRef,
  setInteraction,
}: StartMeasurementMoveInteractionArgs) {
  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  handleCancelDraft();
  const isMovingSelection =
    selectedMeasurementIds.length > 1 &&
    selectedMeasurementIds.includes(measurementId);
  if (event.shiftKey) {
    onSelectMeasurement(measurementId, { shiftKey: true });
    return;
  }

  if (isMovingSelection) {
    const selectedSet = new Set(selectedMeasurementIds.map((id) => String(id)));
    const initialGeometries = measurements
      .filter((measurement) => selectedSet.has(String(measurement._id)))
      .map((measurement) => ({
        measurementId: measurement._id,
        geometry: measurement.geometry,
      }));

    setPreviewGeometries(
      Object.fromEntries(
        initialGeometries.map(({ measurementId: selectedId, geometry: selectedGeometry }) => [
          String(selectedId),
          selectedGeometry,
        ])
      )
    );
    const nextInteraction: InteractionState = {
      mode: "move-selection",
      startPoint: pagePoint,
      initialGeometries,
    };
    interactionRef.current = nextInteraction;
    setInteraction(nextInteraction);
    return;
  }

  onSelectMeasurement(measurementId);
  setPreviewGeometries({ [String(measurementId)]: geometry });
  const nextInteraction: InteractionState = {
    mode: "move",
    measurementId,
    startPoint: pagePoint,
    initialGeometry: geometry,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface StartMeasurementResizeInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  handle: ResizeHandle;
  geometry: MeasurementGeometry;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementResizeInteraction({
  event,
  measurementId,
  handle,
  geometry,
  getPagePoint,
  setPreviewGeometries,
  interactionRef,
  setInteraction,
}: StartMeasurementResizeInteractionArgs) {
  event.stopPropagation();
  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  setPreviewGeometries({ [String(measurementId)]: geometry });
  const nextInteraction: InteractionState = {
    mode: "resize",
    measurementId,
    handle,
    startPoint: pagePoint,
    initialGeometry: geometry,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface StartMeasurementVertexDragInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  vertexIndex: number;
  geometry: PolygonGeometry;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

interface StartMeasurementLabelMoveInteractionArgs {
  event: ReactMouseEvent;
  measurementId: Id<"takeoffPilotMeasurements">;
  annotation: MeasurementAnnotation | undefined;
  getPagePoint: (clientX: number, clientY: number) => Point | null;
  handleCancelDraft: () => void;
  onSelectMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    options?: { shiftKey?: boolean }
  ) => void;
  interactionRef: MutableRefObject<InteractionState | null>;
  setInteraction: Dispatch<SetStateAction<InteractionState | null>>;
}

export function startMeasurementVertexDragInteraction({
  event,
  measurementId,
  vertexIndex,
  geometry,
  setPreviewGeometries,
  interactionRef,
  setInteraction,
}: StartMeasurementVertexDragInteractionArgs) {
  event.stopPropagation();
  setPreviewGeometries({ [String(measurementId)]: geometry });
  const nextInteraction: InteractionState = {
    mode: "vertex",
    measurementId,
    vertexIndex,
    initialGeometry: geometry,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

export function startMeasurementLabelMoveInteraction({
  event,
  measurementId,
  annotation,
  getPagePoint,
  handleCancelDraft,
  onSelectMeasurement,
  interactionRef,
  setInteraction,
}: StartMeasurementLabelMoveInteractionArgs) {
  event.stopPropagation();
  handleCancelDraft();

  if (event.shiftKey) {
    onSelectMeasurement(measurementId, { shiftKey: true });
    return;
  }

  const pagePoint = getPagePoint(event.clientX, event.clientY);
  if (!pagePoint) {
    return;
  }

  onSelectMeasurement(measurementId);
  const nextInteraction: InteractionState = {
    mode: "move-label",
    measurementId,
    startPoint: pagePoint,
    initialAnnotation: annotation,
  };
  interactionRef.current = nextInteraction;
  setInteraction(nextInteraction);
}

interface ApplyTransformPreviewOnPointerMoveArgs {
  activeInteraction: InteractionState;
  event: ReactMouseEvent<HTMLDivElement>;
  pagePoint: Point;
  viewport: DocumentViewportValue;
  measurements: MeasurementOverlayRecord[];
  incrementSnapEnabled: boolean;
  scale: MeasurementScale | null;
  snapSegmentPoint: (anchor: Point, candidate: Point) => Point;
  snapRectangleCorner: (
    anchor: Point,
    candidate: Point,
    lockSquare: boolean
  ) => Point;
  setPreviewGeometries: Dispatch<SetStateAction<PreviewGeometryMap>>;
  setPreviewAnnotations: Dispatch<SetStateAction<Record<string, MeasurementAnnotation>>>;
  updateUnsavedDraft: (nextDraft: DraftMeasurementState) => void;
}

export function applyTransformPreviewOnPointerMove({
  activeInteraction,
  event,
  pagePoint,
  viewport,
  measurements,
  incrementSnapEnabled,
  scale,
  snapSegmentPoint,
  snapRectangleCorner,
  setPreviewGeometries,
  setPreviewAnnotations,
  updateUnsavedDraft,
}: ApplyTransformPreviewOnPointerMoveArgs): boolean {
  if (activeInteraction.mode === "pan") {
    viewport.setPan({
      x: activeInteraction.initialPanX + (event.clientX - activeInteraction.startClientX),
      y: activeInteraction.initialPanY + (event.clientY - activeInteraction.startClientY),
    });
    return true;
  }

  if (activeInteraction.mode === "move") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    setPreviewGeometries({
      [String(activeInteraction.measurementId)]: translateGeometry(
        activeInteraction.initialGeometry,
        dx,
        dy
      ),
    });
    return true;
  }

  if (activeInteraction.mode === "move-selection") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    setPreviewGeometries(
      Object.fromEntries(
        activeInteraction.initialGeometries.map(({ measurementId, geometry }) => [
          String(measurementId),
          translateGeometry(geometry, dx, dy),
        ])
      )
    );
    return true;
  }

  if (activeInteraction.mode === "move-draft") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    const nextDraft: DraftMeasurementState = {
      ...activeInteraction.initialDraft,
      geometry: translateGeometry(activeInteraction.initialDraft.geometry, dx, dy),
    };
    updateUnsavedDraft(nextDraft);
    return true;
  }

  if (activeInteraction.mode === "vertex") {
    let vertexPoint = pagePoint;
    const activeMeasurement = measurements.find(
      (measurement) => measurement._id === activeInteraction.measurementId
    );
    if (
      (activeMeasurement?.tool === TOOL_IDS.line ||
        activeMeasurement?.tool === TOOL_IDS.polyline) &&
      incrementSnapEnabled &&
      scale
    ) {
      const vertices = activeInteraction.initialGeometry.vertices;
      const lastIndex = vertices.length - 1;
      if (activeInteraction.vertexIndex === 0 && vertices.length > 1) {
        vertexPoint = snapSegmentPoint(vertices[1], pagePoint);
      } else if (
        activeInteraction.vertexIndex === lastIndex &&
        activeInteraction.vertexIndex > 0
      ) {
        vertexPoint = snapSegmentPoint(vertices[lastIndex - 1], pagePoint);
      }
    }

    const nextGeometry: PolygonGeometry = {
      type: MEASUREMENT_GEOMETRY_TYPES.poly,
      vertices: activeInteraction.initialGeometry.vertices.map((vertex: Point, index: number) =>
        index === activeInteraction.vertexIndex ? vertexPoint : vertex
      ),
    };
    setPreviewGeometries({ [String(activeInteraction.measurementId)]: nextGeometry });
    return true;
  }

  if (activeInteraction.mode === "resize") {
    if (activeInteraction.initialGeometry.type !== MEASUREMENT_GEOMETRY_TYPES.rect) {
      return true;
    }

    const initialRect = activeInteraction.initialGeometry;
    const right = initialRect.x + initialRect.width;
    const bottom = initialRect.y + initialRect.height;
    let resizedRect: MeasurementGeometry;
    let anchor: Point;
    switch (activeInteraction.handle) {
      case "nw":
        anchor = { x: right, y: bottom };
        resizedRect = normalizeRect(anchor, snapRectangleCorner(anchor, pagePoint, false));
        break;
      case "ne":
        anchor = { x: initialRect.x, y: bottom };
        resizedRect = normalizeRect(anchor, snapRectangleCorner(anchor, pagePoint, false));
        break;
      case "sw":
        anchor = { x: right, y: initialRect.y };
        resizedRect = normalizeRect(anchor, snapRectangleCorner(anchor, pagePoint, false));
        break;
      case "se":
        anchor = { x: initialRect.x, y: initialRect.y };
        resizedRect = normalizeRect(anchor, snapRectangleCorner(anchor, pagePoint, false));
        break;
    }
    setPreviewGeometries({ [String(activeInteraction.measurementId)]: resizedRect });
    return true;
  }

  if (activeInteraction.mode === "move-label") {
    const dx = pagePoint.x - activeInteraction.startPoint.x;
    const dy = pagePoint.y - activeInteraction.startPoint.y;
    setPreviewAnnotations({
      [String(activeInteraction.measurementId)]: {
        ...activeInteraction.initialAnnotation,
        leaderVisible:
          activeInteraction.initialAnnotation?.leaderVisible ?? (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6),
        textOffset: {
          x: (activeInteraction.initialAnnotation?.textOffset?.x ?? 0) + dx,
          y: (activeInteraction.initialAnnotation?.textOffset?.y ?? 0) + dy,
        },
      },
    });
    return true;
  }

  return false;
}
